/**
 * Display formatting.
 *
 * <p>Formatting only. Nothing here computes a monetary figure, a CTR, or a remaining budget — the
 * backend is the sole authority on those, and a second implementation is how a dashboard and an
 * invoice start disagreeing. These functions take a number the server sent and decide how it looks.
 */

const CURRENCY = new Intl.NumberFormat('es-DO', {
  style: 'currency',
  currency: 'DOP',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const NUMBER = new Intl.NumberFormat('es-DO');

const DATE = new Intl.DateTimeFormat('es-DO', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

const DATE_TIME = new Intl.DateTimeFormat('es-DO', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/** Dominican pesos, always with two decimals so columns of money align. */
export function money(amount: number): string {
  return CURRENCY.format(amount);
}

/** Thousands separators, because six-figure impression counts are unreadable without them. */
export function count(value: number): string {
  return NUMBER.format(value);
}

/**
 * A percentage the server already computed.
 *
 * <p>Takes a value like 2.5 and renders "2.50%". It does not divide clicks by impressions: that
 * arithmetic belongs to the backend, which is what an invoice is drawn from.
 */
export function percent(value: number): string {
  return `${value.toFixed(2)}%`;
}

export function date(iso: string): string {
  return DATE.format(new Date(iso));
}

export function dateTime(iso: string): string {
  return DATE_TIME.format(new Date(iso));
}

/** An ISO date for an input[type=date], which only accepts yyyy-mm-dd. */
export function isoDate(value: Date): string {
  return value.toISOString().slice(0, 10);
}

/**
 * A shortened identifier for display.
 *
 * <p>Campaign ids are UUIDs and appear in tables where the full value would dominate the row. The
 * first segment is enough to recognise one at a glance, and the full value stays available on
 * hover.
 */
export function shortId(id: string): string {
  return id.slice(0, 8);
}

/** How much of a budget has been spent, as a percentage capped at 100 for a progress bar. */
export function budgetProgress(consumed: number, total: number): number {
  if (total <= 0) {
    return 0;
  }
  return Math.min(100, (consumed / total) * 100);
}
